// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * The artefacts.
 *
 * The pinned requirements file is the one that could mislead, so it carries its
 * own limitation in a comment at the top: `pip install --require-hashes` needs
 * EVERY dependency pinned and hashed, transitive ones included, and ghostdep
 * only ever sees the names you pasted. Handing someone a file that fails on
 * first use — with a pip error that does not explain why — would be worse than
 * handing them nothing, so the file says what it is and what to run instead for
 * a complete lock.
 *
 * Each artefact is downloaded by its own explicit click rather than as a
 * bundle. Safari silently suppresses every programmatic download after the
 * first in a single gesture, and Chrome turns the second into a permission
 * prompt, so a one-click "download all" quietly delivers one file out of four.
 */

import type { Finding } from './types.ts';
import { DEFAULT_THRESHOLDS, daysSince, type Thresholds } from './score.ts';

const GENERATOR = 'ghostdep — https://ghostdep.benrichardson.dev';

/* ── CSV ──────────────────────────────────────────────────────────────────── */

/**
 * Escape the control characters that break a CSV reader.
 *
 * Written as a code-point test rather than a regex character class ON PURPOSE.
 * The obvious `/[\x00-\x1f]/` has to be typed with real escape sequences, and
 * the first draft of this file was written with the LITERAL bytes instead —
 * invisible in a diff, in review and in a terminal, and it silently changes
 * what the class matches. The source-hygiene test that fails the build on any
 * literal control byte under src/ is what caught it. Comparing numbers cannot
 * go wrong the same way.
 */
function escapeControls(text: string): string {
  let out = '';
  for (const ch of text) {
    const code = ch.codePointAt(0) ?? 0;
    if (code === 10 || code === 13) {
      // One record must always be one physical line, so a newline becomes a
      // visible escape rather than a real break.
      out += '\\n';
    } else if (code < 32 || code === 127) {
      out += `\\x${code.toString(16).padStart(2, '0')}`;
    } else {
      out += ch;
    }
  }
  return out;
}

/**
 * RFC 4180, plus the thing that bites in practice: a leading `=`, `+`, `-` or
 * `@` is a formula in Excel, Sheets and LibreOffice, and these fields carry
 * package names chosen by somebody else.
 */
export function csvField(value: string | number | null | undefined, delimiter = ','): string {
  if (value === null || value === undefined) return '';
  const text = escapeControls(String(value));
  const injectable = /^[=+\-@\t]/.test(text);
  // The apostrophe alone is not enough: unquoted, some importers strip it and
  // restore the formula. Prefix AND force quoting.
  if (injectable) return `"'${text.replace(/"/g, '""')}"`;
  // `;` is quoted too, because Excel in de/fr/es locales splits on it.
  if (text.includes('"') || text.includes(delimiter) || text.includes(';')) return `"${text.replace(/"/g, '""')}"`;
  return text;
}

export function toCsv(rows: readonly (readonly (string | number | null)[])[], delimiter = ','): string {
  // UTF-8 BOM and CRLF, because the audience opens this in Excel as often as in a terminal.
  return `﻿${rows.map((row) => row.map((cell) => csvField(cell, delimiter)).join(delimiter)).join('\r\n')}\r\n`;
}

export function auditCsv(findings: readonly Finding[], now: number): string {
  const rows: (string | number | null)[][] = [
    [
      'ecosystem',
      'name',
      'as_written',
      'found_in',
      'verdict',
      'exists',
      'latest_version',
      'first_published',
      'age_days',
      'weekly_downloads',
      'releases',
      'repository',
      'install_script',
      'integrity',
      'nearest_popular_name',
      'edit_distance',
      'note',
    ],
  ];
  for (const f of findings) {
    const near = f.neighbours[0];
    rows.push([
      f.candidate.ecosystem,
      f.candidate.name,
      f.candidate.raw,
      f.candidate.origin,
      f.verdict,
      f.facts.existence,
      f.facts.latestVersion,
      f.facts.firstPublished,
      daysSince(f.facts.firstPublished, now),
      f.facts.weeklyDownloads,
      f.facts.releaseCount,
      f.facts.repository,
      f.facts.hasInstallScript === null ? '' : String(f.facts.hasInstallScript),
      f.facts.integrity,
      near ? near.name : '',
      near ? (near.cost / 2).toString() : '',
      f.reason,
    ]);
  }
  return toCsv(rows);
}

/* ── pinned requirements ──────────────────────────────────────────────────── */

export function requirementsTxt(findings: readonly Finding[]): string {
  const pypi = findings.filter((f) => f.candidate.ecosystem === 'pypi');
  const usable = pypi.filter((f) => f.facts.existence === 'exists' && f.facts.latestVersion);
  const skipped = pypi.filter((f) => !usable.includes(f));

  const lines: string[] = [
    `# Generated by ${GENERATOR}`,
    '#',
    '# Every package below was confirmed to exist on PyPI and is pinned to the',
    '# version that was current when this file was written, with the sha256 of',
    '# every distribution file PyPI publishes for that version.',
    '#',
    '# READ THIS BEFORE USING --require-hashes:',
    '#   pip --require-hashes demands that EVERY package it installs is pinned',
    '#   and hashed, transitive dependencies included. ghostdep only sees the',
    '#   names you gave it, so this file is almost certainly incomplete for that',
    '#   mode and pip will refuse it with "In --require-hashes mode, all',
    '#   requirements must have their versions pinned".',
    '#',
    '#   For a complete lock, feed these names to a resolver:',
    '#     pip-compile --generate-hashes requirements.in',
    '#     uv pip compile --generate-hashes requirements.in',
    '#',
    '#   Without --require-hashes this works as an ordinary pinned',
    '#   requirements.txt, and pip still checks the hashes of these packages.',
    '',
  ];

  for (const f of usable) {
    const digests = f.facts.sha256;
    const extras = /\[.*?\]$/.exec(f.candidate.raw)?.[0] ?? '';
    if (!digests.length) {
      lines.push(`# ${f.candidate.name}: PyPI published no sha256 for ${f.facts.latestVersion}, pinned without a hash`);
      lines.push(`${f.candidate.name}${extras}==${f.facts.latestVersion}`);
      lines.push('');
      continue;
    }
    lines.push(`${f.candidate.name}${extras}==${f.facts.latestVersion} \\`);
    digests.forEach((d, index) => {
      const last = index === digests.length - 1;
      lines.push(`    --hash=sha256:${d.sha256}${last ? '' : ' \\'}`);
    });
    lines.push('');
  }

  if (skipped.length) {
    lines.push('# NOT PINNED — these were not confirmed to exist, or had no version:');
    for (const f of skipped) lines.push(`#   ${f.candidate.name}  (${f.verdict})`);
    lines.push('');
  }
  return lines.join('\n');
}

/* ── pinned package.json ──────────────────────────────────────────────────── */

export function packageJson(findings: readonly Finding[]): string {
  const npm = findings.filter((f) => f.candidate.ecosystem === 'npm' && f.facts.existence === 'exists');
  const dependencies: Record<string, string> = {};
  for (const f of [...npm].sort((a, b) => a.candidate.name.localeCompare(b.candidate.name))) {
    if (f.facts.latestVersion) dependencies[f.candidate.name] = f.facts.latestVersion;
  }
  const doc = {
    _comment: [
      `Generated by ${GENERATOR}.`,
      'Exact versions, no ranges: every name here was confirmed to exist on the npm registry.',
      "npm's integrity hashes live in package-lock.json, which describes your whole RESOLVED tree.",
      'ghostdep only sees the names you pasted, so it does not write a lockfile it would have to',
      'invent. Run `npm install` to generate a correct one; the top-level integrity values it should',
      'produce are in dependency-audit.csv so you can compare them.',
    ],
    dependencies,
  };
  return `${JSON.stringify(doc, null, 2)}\n`;
}

/* ── unresolved ───────────────────────────────────────────────────────────── */

export function unresolvedTxt(findings: readonly Finding[]): string {
  const missing = findings.filter((f) => f.verdict === 'not-in-registry');
  const unchecked = findings.filter((f) => f.verdict === 'not-checked');

  const lines = [`# Generated by ${GENERATOR}`, ''];
  if (!missing.length) {
    lines.push('Every name checked was found in its registry.', '');
  } else {
    lines.push(`${missing.length} name${missing.length === 1 ? '' : 's'} could not be found in the registry:`, '');
    for (const f of missing) {
      lines.push(`${f.candidate.name}  (${f.candidate.ecosystem})`);
      lines.push(`    written as:  ${f.candidate.raw}`);
      lines.push(`    found in:    ${f.candidate.origin} — ${f.candidate.evidence}`);
      if (f.neighbours.length) {
        lines.push('    closest existing names:');
        for (const n of f.neighbours) {
          lines.push(`        ${n.name}  (${(n.cost / 2).toString()} edits away, popularity rank ${n.rank + 1})`);
        }
      } else {
        lines.push('    no similarly-spelled package in the popular-name table');
      }
      lines.push('');
    }
  }
  if (unchecked.length) {
    lines.push('These could not be checked at all — this is not a statement that they are missing:', '');
    for (const f of unchecked) {
      lines.push(`${f.candidate.name}  (${f.candidate.ecosystem}) — ${f.facts.error ?? 'lookup failed'}`);
    }
    lines.push('');
  }
  return lines.join('\n');
}

/* ── report ───────────────────────────────────────────────────────────────── */

const ESCAPE: Record<string, string> = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };

/**
 * Everything interpolated below is third-party text — package names from a
 * registry, and lines from a document the user did not write. The report is
 * opened from file://, so it escapes on the way in rather than trusting it.
 */
function esc(value: unknown): string {
  return String(value ?? '').replace(/[&<>"']/g, (c) => ESCAPE[c]);
}

const VERDICT_LABEL: Record<string, string> = {
  'not-in-registry': 'Not in the registry',
  'very-new': 'Newly published',
  'near-popular-name': 'Close to a bigger name',
  unpopular: 'Little used',
  established: 'Established',
  'not-checked': 'Not checked',
};

export function reportHtml(
  findings: readonly Finding[],
  summary: string,
  thresholds: Thresholds = DEFAULT_THRESHOLDS,
  now = Date.now(),
  stamp = new Date(now).toISOString(),
): string {
  const rows = findings
    .map((f) => {
      const near = f.neighbours[0];
      const age = daysSince(f.facts.firstPublished, now);
      return `<tr class="v-${esc(f.verdict)}">
      <td><code>${esc(f.candidate.name)}</code></td>
      <td>${esc(f.candidate.ecosystem)}</td>
      <td>${esc(VERDICT_LABEL[f.verdict] ?? f.verdict)}</td>
      <td>${esc(f.facts.latestVersion ?? '—')}</td>
      <td>${age === null ? '—' : esc(`${age} days`)}</td>
      <td>${f.facts.weeklyDownloads === null ? '—' : esc(f.facts.weeklyDownloads.toLocaleString())}</td>
      <td>${near ? `${esc(near.name)} <span class="muted">(${esc((near.cost / 2).toString())} edits)</span>` : '—'}</td>
      <td>${esc(f.reason)}</td>
    </tr>`;
    })
    .join('\n');

  // No <script>, no <link>, no src. A self-contained file with no network
  // capability, asserted by test.
  return `<!doctype html>
<html lang="en"><head><meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>ghostdep audit</title>
<style>
:root{color-scheme:light dark;--fg:#16171d;--dim:#5c6070;--line:#d9dbe3;--bg:#fff}
@media (prefers-color-scheme:dark){:root{--fg:#e8e9ef;--dim:#9297a8;--line:#2b2e3a;--bg:#101218}}
body{font:14px/1.55 -apple-system,BlinkMacSystemFont,'Segoe UI',Helvetica,Arial,sans-serif;color:var(--fg);background:var(--bg);margin:0;padding:2rem 1.25rem;max-width:70rem}
h1{font-size:1.25rem;margin:0 0 .25rem}
.sum{font-size:1rem;margin:0 0 1.5rem}
.muted{color:var(--dim)}
table{border-collapse:collapse;width:100%;font-size:13px}
th,td{text-align:left;padding:.45rem .6rem;border-bottom:1px solid var(--line);vertical-align:top}
th{font-weight:600;font-size:11px;text-transform:uppercase;letter-spacing:.04em;color:var(--dim)}
code{font:12px ui-monospace,'SF Mono',Menlo,Consolas,monospace}
.v-not-in-registry td:first-child code{font-weight:700}
footer{margin-top:2rem;padding-top:1rem;border-top:1px solid var(--line);color:var(--dim);font-size:12px}
.wrap{overflow-x:auto}
</style></head><body>
<h1>ghostdep audit</h1>
<p class="sum">${esc(summary)}</p>
<div class="wrap"><table>
<thead><tr><th>Package</th><th>Registry</th><th>Verdict</th><th>Latest</th><th>Age</th><th>Weekly downloads</th><th>Nearest popular name</th><th>Evidence</th></tr></thead>
<tbody>
${rows}
</tbody></table></div>
<footer>
<p>Generated ${esc(stamp)} by <strong>ghostdep</strong>, entirely in a browser tab. Thresholds: one edit permitted per ${esc(thresholds.lengthPerEdit)} characters of name length; a neighbour is mentioned only when it is at least ${esc(thresholds.popularityRatio)}&times; more popular.</p>
<p>ghostdep reports what the public registries say. It does not and cannot tell you whether a package is malicious, and an established package is not thereby a safe one. PyPI download counts are absent because pypistats.org serves no CORS header, so they cannot be read from a browser.</p>
</footer>
</body></html>
`;
}
